<template>
  <div>
    <div class="w-full h-[176px] p-[9px] bg-[#FAFAFA] rounded-md mb-6">
      <div
        v-if="multipleItemsSelected"
        class="w-full h-full flex items-center justify-center relative"
      >
        <ThumbnailIcon
          v-for="(selectedItem, index) in lastThreeItems"
          :key="selectedItem.id"
          class="w-[140px] h-[140px] absolute"
          :style="getStackedThumbnailStyles(index)"
          :icon-size="140"
          :type="getItemType(selectedItem)"
          :image-url="getImageUrl(getItemPreviewUrl(selectedItem))"
          showShadow
        />
      </div>
      <ThumbnailIcon
        v-else
        class="w-full h-full"
        :class="previewClasses"
        :icon-size="140"
        :type="itemType"
        :image-url="getImageUrl(singleItemPreviewUrl)"
        showShadow
        @click="$emit('previewClick')"
      />
    </div>

    <template v-if="!multipleItemsSelected">
      <div v-if="isFileItem" class="flex gap-x-2.5 mb-6">
        <div v-tooltip="'Coming soon'" class="w-1/2">
          <UiButton icon="Coin" disabled> Sell / Transfer </UiButton>
        </div>
        <div v-tooltip="'Coming soon'" class="w-1/2">
          <UiButton icon="License" class="w-full" disabled> License </UiButton>
        </div>
      </div>

      <div
        v-if="itemPath"
        class="w-full h-[30px] font-primary border-b border-[#E5E5E5] flex items-center justify-between text-[12px] leading-[16px] text-gray-800"
      >
        <div class="opacity-60">Located in</div>
        <div>
          <Breadcrumb
            class="text-[12px] leading-[16px] tracking-[-0.16px] font-normal!"
            :path="itemPath"
            :iconSize="12"
            dropdownPosition="top-right"
            activeLocationClass=""
            @location="$emit('locationUpdate', $event)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ThumbnailIcon } from '@dacr/ui'
import { UiButton } from '@dacr/ui'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile } from '@/features/explorer/utils/itemGuards'
import type { SidebarItem } from './Sidebar.vue'
import { useExplorerStore } from '@/features/explorer/store'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { computed } from 'vue'
import Breadcrumb from '@/shared/components/Breadcrumb.vue'

const props = defineProps<{
  item: SidebarItem
  selectedItems: Item[]
  isFileItem: boolean
  isItemInactive: boolean
}>()

defineEmits<{
  previewClick: []
  locationUpdate: [path: string[]]
}>()

const explorerStore = useExplorerStore()

const multipleItemsSelected = computed(() => props.selectedItems.length > 1)

const lastThreeItems = computed(() => props.selectedItems.slice(-3))

const getItemType = (item: Item) => (isFile(item) ? item.type : 'folder')

const getItemPreviewUrl = (item: Item) =>
  isFile(item) ? item.asset?.previewImageUrl : undefined

const singleItemPreviewUrl = computed(() => {
  if (!props.item || !('__typename' in props.item)) return undefined
  return isFile(props.item as Item)
    ? (props.item as Item & { __typename: 'File' }).asset?.previewImageUrl
    : undefined
})

const itemType = computed(() => {
  if (!props.item || !('__typename' in props.item)) return 'folder'
  return isFile(props.item as Item)
    ? (props.item as Item & { __typename: 'File' }).type
    : 'folder'
})

const itemPath = computed(() => {
  const folderPath = explorerStore.currentFolder?.path
  if (!folderPath || folderPath === '/') return undefined

  const path = folderPath.split('/').filter(Boolean)
  return ['Dacr'].concat(path || [])
})

const previewClasses = computed(() => ({
  ...(props.isFileItem &&
    !props.isItemInactive && {
      'cursor-pointer': true,
    }),
}))

const getStackedThumbnailStyles = (index: number) => {
  const rotate =
    index === 0 ? -8 : index === lastThreeItems.value.length - 1 ? 0 : 8

  return {
    transform: `rotate(${rotate}deg)`,
  }
}

const getImageUrl = (imageUrl?: string | null) => {
  if (!imageUrl) return null

  return applyAuthorizationToken(imageUrl, [{ key: 'width', value: 300 }])
}
</script>
